import styles from './style.postcss';

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import classnames from 'classnames';
import { Map, List } from 'immutable';
import is from 'is_js';

import { connect,
  HorizontalSelect,
  StudentCard,
  Card,
  Button,
  PageCard,
  testClass } from 'omni-common-ui';
import { actions as GroupMembers } from 'data/GroupMembers';
import { actions as ketExamsActions,
  accessors as ketExamsAccessors,
  KET_EXAM_TYPE_MOCK } from 'data/KetExams';

import MarkInput from './MarkInput';

const EMPTY_HYPHEN = '-';
const NO_MATCH_SLASH = '/';
const INTEGER_PATTERN = /^\d+$/;

export class KetExamMarkView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      marks: new Map(),
      isSaving: false,
    };

    this._onSave = this._onSave.bind(this);
    this._getLinkHrefForValue = this._getLinkHrefForValue.bind(this);
  }

  componentWillMount() {
    this._initMarks(this.props);
  }

  componentWillReceiveProps(nextProps) {
    const studentChanged = nextProps.params.studentId !== this.props.params.studentId;
    const examChanged = this._getExamData(nextProps) !== this._getExamData(this.props);
    if (studentChanged || examChanged) {
      this._initMarks(nextProps);
    }
  }

  _getExamData(props = this.props) {
    const { exam } = props;
    return exam && exam.data;
  }

  _initMarks(props) {
    const examData = this._getExamData(props);
    if (! examData) {
      this.setState({ marks: new Map() });
      return;
    }
    const result = examData.studentResults.get(`${props.params.studentId}`);
    const marks = new Map(examData.skills.map((skill) => {
      const skillResult = result && result.skillResults.get(skill.skillId);
      const mark = skillResult && skillResult.get('mark');
      return [skill.skillId, is.number(mark) ? `${mark}` : ''];
    }));
    this.setState({ marks });
  }

  _getMarkRoute(studentId) {
    const { params: { groupId, mockIndex }, examType, buildRoute } = this.props;
    if (examType === KET_EXAM_TYPE_MOCK) {
      return buildRoute(`/group/${groupId}/ket-exam/${mockIndex || '1'}/student/${studentId}`);
    }
    return buildRoute(`/group/${groupId}/ket-score/student/${studentId}`);
  }

  _getLinkHrefForValue(studentId) {
    return this._getMarkRoute(studentId);
  }

  _getParsedMark(skill) {
    const markText = this.state.marks.get(skill.skillId);
    if (! INTEGER_PATTERN.test(markText || '')) return undefined;
    return Number.parseInt(markText, 10);
  }

  _computeSkillDerived(skill) {
    const examData = this._getExamData();
    const mark = this._getParsedMark(skill);
    const scale = ketExamsAccessors.mapMarkToScale(skill, mark);
    const banding = ketExamsAccessors.mapScaleToBanding(
      examData.scaleBandings, scale);
    return { mark, scale, banding };
  }

  _computeOverallDerived(skills) {
    const examData = this._getExamData();
    const scales = skills
      .map((skill) => {
        const { scale } = this._computeSkillDerived(skill);
        return is.number(scale) ? scale : 0;
      })
      .toArray();
    const overallScore = ketExamsAccessors.computeOverallScore(scales);
    const overallBanding = ketExamsAccessors.mapScaleToBanding(
      examData.scaleBandings, overallScore);
    return { overallScore, overallBanding };
  }

  _areAllMarksFilled(skills) {
    return skills.every((skill) => is.number(this._getParsedMark(skill)));
  }

  _onMarkChange(skill, value) {
    this.setState(({ marks }) => ({ marks: marks.set(skill.skillId, value) }));
  }

  _onSave() {
    const { params: { groupId, studentId }, currentExamPlan } = this.props;
    const examData = this._getExamData();
    const skillMarks = examData.skills
      .map((skill) => ({
        skillId: skill.skillId,
        mark: this._getParsedMark(skill),
      }))
      .toArray();

    this.setState({ isSaving: true });
    this.props.submitKetExamResult(
      groupId, currentExamPlan.get('ketExamId'), studentId, skillMarks)
      .then(() => this.setState({ isSaving: false }))
      .catch(() => this.setState({ isSaving: false }));
  }

  _renderStudentSelect() {
    const { members, params: { studentId } } = this.props;
    const examData = this._getExamData();
    const options = members.map((member) => {
      const { studentGeneralInfo } = member;
      const result = examData.studentResults.get(`${studentGeneralInfo.id}`);
      const hasFinalScore = result && is.number(result.finalScore);
      const finalScore = hasFinalScore ? result.finalScore : EMPTY_HYPHEN;
      const banding = hasFinalScore ?
        ketExamsAccessors.mapScaleToBanding(examData.scaleBandings, result.finalScore) :
        undefined;
      const isFail = banding && ketExamsAccessors.isFailBanding(banding.grade);
      const html = <StudentCard vertical borderless backgroundless>
        <StudentCard.Profile name={studentGeneralInfo.firstName}
            surname={studentGeneralInfo.lastName}
            localName={studentGeneralInfo.localName}
            avatarUrl={studentGeneralInfo.avatarUrl}
            gender={studentGeneralInfo.gender} />
        <StudentCard.Content>
          <Card.Content className={classnames(styles.KetExamMarkView_studentSelect_score,
              { [styles.__fail]: isFail })}>
            {finalScore}
          </Card.Content>
        </StudentCard.Content>
      </StudentCard>;
      return {
        html,
        value: studentGeneralInfo.id,
        className: styles.KetExamMarkView_studentSelect_option,
      };
    });

    return <div className={styles.KetExamMarkView_studentSelect}>
      <HorizontalSelect options={options}
          value={studentId}
          getLinkHrefForValue={this._getLinkHrefForValue} />
    </div>;
  }

  _renderSkillColumn(skill) {
    const { mark, scale, banding } = this._computeSkillDerived(skill);
    const hasMark = is.number(mark);
    let scaleText = EMPTY_HYPHEN;
    if (is.number(scale)) {
      scaleText = `${scale}`;
    } else if (hasMark) {
      scaleText = NO_MATCH_SLASH;
    }
    let bandingText = EMPTY_HYPHEN;
    if (banding) {
      bandingText = ketExamsAccessors.formatGradeBanding(banding.grade, banding.level);
    } else if (hasMark) {
      bandingText = NO_MATCH_SLASH;
    }
    const isFail = banding && ketExamsAccessors.isFailBanding(banding.grade);

    return <div key={skill.skillId} className={styles.KetExamMarkView_skill}>
      <div className={styles.KetExamMarkView_skill_name}>{skill.name}</div>
      <div className={styles.KetExamMarkView_skill_field}>
        <div className={styles.KetExamMarkView_skill_field_label}>Marks</div>
        <MarkInput value={this.state.marks.get(skill.skillId) || ''}
            maxMark={skill.maxMark}
            onChange={(value) => this._onMarkChange(skill, value)} />
      </div>
      <div className={styles.KetExamMarkView_skill_field}>
        <div className={styles.KetExamMarkView_skill_field_label}>
          Cambridge English Scale
        </div>
        <input type="text"
            readOnly
            className={styles.KetExamMarkView_readonlyInput}
            value={scaleText} />
      </div>
      <div className={styles.KetExamMarkView_skill_field}>
        <div className={styles.KetExamMarkView_skill_field_label}>Grade Banding</div>
        <input type="text"
            readOnly
            className={classnames(styles.KetExamMarkView_readonlyInput,
              { [styles.__fail]: isFail })}
            value={bandingText} />
      </div>
    </div>;
  }

  _renderSummary(skills) {
    const allMarksFilled = this._areAllMarksFilled(skills);
    const canSave = allMarksFilled && ! this.state.isSaving;
    const { overallScore, overallBanding } = allMarksFilled ?
      this._computeOverallDerived(skills) :
      {};
    let resultText = EMPTY_HYPHEN;
    if (overallBanding) {
      resultText = is.existy(overallBanding.grade) ? overallBanding.grade : NO_MATCH_SLASH;
    }
    const isOverallFail = overallBanding && ketExamsAccessors.isFailBanding(overallBanding.grade);

    return <div className={styles.KetExamMarkView_summary}>
      <div className={styles.KetExamMarkView_summary_fields}>
        <div className={styles.KetExamMarkView_summary_item}>
          <div className={styles.KetExamMarkView_summary_item_label}>Result</div>
          <input type="text"
              readOnly
              className={styles.KetExamMarkView_summary_item_input}
              value={resultText} />
        </div>
        <div className={styles.KetExamMarkView_summary_item}>
          <div className={styles.KetExamMarkView_summary_item_label}>Overall Score</div>
          <input type="text"
              readOnly
              className={classnames(styles.KetExamMarkView_summary_item_input,
                { [styles.__fail]: isOverallFail })}
              value={is.number(overallScore) ? `${overallScore}` : EMPTY_HYPHEN} />
        </div>
        <div className={styles.KetExamMarkView_summary_item}>
          <div className={styles.KetExamMarkView_summary_item_label}>CEFR Level</div>
          <input type="text"
              readOnly
              className={styles.KetExamMarkView_summary_item_input}
              value={(overallBanding && overallBanding.level) || EMPTY_HYPHEN} />
        </div>
      </div>
      <Button className={classnames(styles.KetExamMarkView_summary_save,
            testClass('ket-save-button'))}
          disabled={! canSave}
          onClick={this._onSave}>
        Save
      </Button>
    </div>;
  }

  render() {
    const { members } = this.props;
    const examData = this._getExamData();
    if (! examData || ! List.isList(members) || members.size === 0) return null;

    const skills = examData.skills.sortBy((skill) => skill.orderingSequence);

    return <div className={styles.KetExamMarkView}>
      {this._renderStudentSelect()}
      <div className={styles.KetExamMarkView_panel}>
        <PageCard>
          <PageCard.Heading text="Mark Scores" />
          <PageCard.Content>
            <div className={styles.KetExamMarkView_skills}>
              {skills.map((skill) => this._renderSkillColumn(skill))}
            </div>
            {this._renderSummary(skills)}
          </PageCard.Content>
        </PageCard>
      </div>
    </div>;
  }
}

KetExamMarkView.propTypes = {
  params: PropTypes.object.isRequired,
  examType: PropTypes.string,
  exam: PropTypes.object,
  currentExamPlan: PropTypes.object,
  members: PropTypes.object,
  buildRoute: PropTypes.func.isRequired,
  submitKetExamResult: PropTypes.func.isRequired,
};

export function mapStateToProps(state, ownProps) {
  const { groupId } = ownProps.params;
  return {
    members: GroupMembers.getMembersByGroup(state, groupId),
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(ketExamsActions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(KetExamMarkView);



// WEBPACK FOOTER //
// ./containers/Group/KetExam/MarkView/component.jsx